"use client";

import React, { useState } from "react";
import { Search } from "lucide-react";
import { GlobalSearchCommand } from "./GlobalSearchCommand";

interface HeaderSearchProps {
  currentLanguage: "en" | "mm";
  /**
   * Surface tone the button sits on:
   *   - "dark"  → navy / gradient bar (light text + translucent border)
   *   - "light" → white brand row / minimal bar (ink text + hairline)
   */
  tone?: "dark" | "light";
  /** Compact icon-only variant (minimal header). */
  compact?: boolean;
  className?: string;
}

/**
 * Epona header search trigger.
 *
 * A styled button showing a magnifier + "Search" label + a ⌘K hint
 * chip. Clicking it — or pressing Cmd/Ctrl+K anywhere — opens the
 * global `GlobalSearchCommand` palette (which itself owns the keyboard
 * shortcut). This component holds the shared `open` state so both entry
 * points drive the same dialog.
 */
export function HeaderSearch({
  currentLanguage,
  tone = "dark",
  compact = false,
  className = "",
}: HeaderSearchProps) {
  const [open, setOpen] = useState(false);

  const label = currentLanguage === "mm" ? "ရှာဖွေရန်" : "Search";
  const isDark = tone === "dark";

  // Icon-only button — used by the minimal header.
  if (compact) {
    return (
      <>
        <button
          type="button"
          aria-label={label}
          onClick={() => setOpen(true)}
          className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
            isDark
              ? "text-white/90 hover:bg-white/10"
              : "border border-[color:var(--color-border)] text-[color:var(--dental06-navy)] hover:bg-[color:var(--dental06-navy-tint)] hover:text-[color:var(--dental06-blue)]"
          } ${className}`}
        >
          <Search className="h-[18px] w-[18px]" />
        </button>
        <GlobalSearchCommand
          open={open}
          onOpenChange={setOpen}
          currentLanguage={currentLanguage}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label={label}
        onClick={() => setOpen(true)}
        className={`group inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition-colors ${
          isDark
            ? "border border-white/25 bg-white/5 text-white/85 hover:border-white/50 hover:text-white"
            : "border border-border bg-background text-muted-foreground hover:border-[color:var(--color-accent)] hover:text-[color:var(--color-primary)]"
        } ${className}`}
        style={{ fontFamily: "var(--font-sans)" }}
      >
        <Search className="h-4 w-4 flex-shrink-0" />
        <span>{label}</span>
      </button>
      <GlobalSearchCommand
        open={open}
        onOpenChange={setOpen}
        currentLanguage={currentLanguage}
      />
    </>
  );
}

export default HeaderSearch;
